import React, { useState, useEffect } from 'react';
import { Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { TagConfig } from './types';
import { useFetchAppTags } from '@/hooks/useAppTags';

interface TagsConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig?: TagConfig;
  onSave: (config: TagConfig) => void;
}

// Generate quarters from 2023Q1 to current
function generateQuarters(): string[] {
  const quarters: string[] = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);

  for (let year = 2023; year <= currentYear; year++) {
    const maxQuarter = year === currentYear ? currentQuarter : 4;
    for (let q = 1; q <= maxQuarter; q++) {
      quarters.push(`${year}Q${q}`);
    }
  }

  return quarters.reverse(); // Most recent first
}

export const TagsConfigModal: React.FC<TagsConfigModalProps> = ({
  open,
  onOpenChange,
  initialConfig,
  onSave,
}) => {
  const [selectedTagId, setSelectedTagId] = useState<string>('');
  const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(new Set());
  const [availableQuarters] = useState(generateQuarters());

  const { data: tags, isLoading: isLoadingTags } = useFetchAppTags();

  // Initialize form with existing config
  useEffect(() => {
    if (open) {
      if (initialConfig) {
        setSelectedTagId(initialConfig.tagId);
        setSelectedPeriods(new Set(initialConfig.periods));
      } else {
        // Reset to defaults - select current quarter
        setSelectedTagId('');
        setSelectedPeriods(new Set([availableQuarters[0]]));
      }
    }
  }, [initialConfig, open, availableQuarters]);

  const handlePeriodToggle = (period: string) => {
    const newPeriods = new Set(selectedPeriods);
    if (newPeriods.has(period)) {
      newPeriods.delete(period);
    } else {
      newPeriods.add(period);
    }
    setSelectedPeriods(newPeriods);
  };

  const handleSelectAll = () => {
    setSelectedPeriods(new Set(availableQuarters));
  };

  const handleClearAll = () => {
    setSelectedPeriods(new Set());
  };

  const handleSave = () => {
    if (!selectedTagId || selectedPeriods.size === 0) {
      return;
    }

    const selectedTag = tags?.find(t => t.id === selectedTagId);
    if (!selectedTag) return;

    const config: TagConfig = {
      tagId: selectedTagId,
      tagName: selectedTag.tag_name,
      periods: Array.from(selectedPeriods).sort((a, b) => b.localeCompare(a)) // Most recent first
    };

    onSave(config);
    onOpenChange(false);
  };

  const canSave = selectedTagId && selectedPeriods.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Tags Analytics</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tag Selection */}
          <div className="space-y-3">
            <Label>Select Tag</Label>
            {isLoadingTags ? (
              <div className="text-sm text-gray-500">Loading tags...</div>
            ) : tags && tags.length > 0 ? (
              <Select value={selectedTagId} onValueChange={setSelectedTagId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tag..." />
                </SelectTrigger>
                <SelectContent>
                  {[...tags]
                    .sort((a, b) => a.tag_name.localeCompare(b.tag_name))
                    .map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        {tag.tag_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-gray-500">No tags available</div>
            )}
          </div>

          {/* Period Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Select Periods</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 max-h-[200px] overflow-y-auto border rounded-lg p-3">
              {availableQuarters.map((quarter) => (
                <label
                  key={quarter}
                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-all ${
                    selectedPeriods.has(quarter)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Checkbox
                    checked={selectedPeriods.has(quarter)}
                    onCheckedChange={() => handlePeriodToggle(quarter)}
                  />
                  <span className="text-sm font-medium">{quarter}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {selectedPeriods.size} period{selectedPeriods.size !== 1 ? 's' : ''} selected
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save Tags Analytics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
